<template>
  <v-dialog
    :model-value="modelValue"
    max-width="560"
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="pa-2">
      <v-card-title class="text-h6">
        {{ $t("acceptance.weighings.dialog.title") }}
      </v-card-title>
      <v-card-subtitle class="acceptance-wrap">
        {{ $t("acceptance.weighings.dialog.subtitle") }}
      </v-card-subtitle>

      <v-card-text>
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-4"
        >
          {{ error }}
        </v-alert>

        <!-- Method first: its fields appear only once it is chosen. -->
        <div class="text-body-2 mb-1">
          {{ $t("acceptance.weighings.dialog.method") }} *
        </div>
        <v-btn-toggle
          :model-value="form.method"
          color="primary"
          variant="outlined"
          density="comfortable"
          divided
          :disabled="saving"
          class="mb-4 acceptance-wrap"
          @update:model-value="setMethod"
        >
          <v-btn value="gross_tare">
            {{ $t("acceptance.weighings.methods.gross_tare") }}
          </v-btn>
          <v-btn value="direct">
            {{ $t("acceptance.weighings.methods.direct") }}
          </v-btn>
        </v-btn-toggle>

        <div v-if="!form.method" class="text-body-2 text-grey-darken-1">
          {{ $t("acceptance.weighings.dialog.choose_method") }}
        </div>

        <v-row v-else dense>
          <template v-if="form.method === 'gross_tare'">
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.gross"
                type="number"
                min="0"
                step="0.1"
                variant="outlined"
                density="comfortable"
                :label="`${$t('acceptance.weighings.dialog.gross')} *`"
                :error-messages="message('gross')"
                :disabled="saving"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.tare"
                type="number"
                min="0"
                step="0.1"
                variant="outlined"
                density="comfortable"
                :label="`${$t('acceptance.weighings.dialog.tare')} *`"
                :error-messages="message('tare')"
                :disabled="saving"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <div class="text-caption text-grey-darken-1">
                {{ $t("acceptance.weighings.dialog.net_calculated") }}
              </div>
              <div class="text-h6">{{ netLabel }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.ticketNumber"
                variant="outlined"
                density="comfortable"
                maxlength="60"
                :label="`${$t('acceptance.weighings.dialog.ticket_number')} *`"
                :error-messages="message('ticketNumber')"
                :disabled="saving"
              />
            </v-col>
          </template>

          <template v-else>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.net"
                type="number"
                min="0"
                step="0.1"
                variant="outlined"
                density="comfortable"
                :label="`${$t('acceptance.weighings.dialog.net_direct')} *`"
                :error-messages="message('net')"
                :disabled="saving"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.device"
                variant="outlined"
                density="comfortable"
                maxlength="120"
                :label="`${$t('acceptance.weighings.dialog.device')} *`"
                :error-messages="message('device')"
                :disabled="saving"
              />
            </v-col>
          </template>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="form.weighedAt"
              type="datetime-local"
              variant="outlined"
              density="comfortable"
              :max="maxWeighedAt"
              :label="`${$t('acceptance.weighings.dialog.weighed_at')} *`"
              :error-messages="message('weighedAt')"
              :disabled="saving"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <div class="text-caption text-grey-darken-1">
              {{ $t("acceptance.weighings.dialog.verified_by") }}
            </div>
            <div class="text-body-2 mt-1">
              {{ verifier }} ·
              {{ $t("acceptance.weighings.dialog.logged_in_user") }}
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          variant="outlined"
          color="primary"
          :disabled="saving"
          @click="$emit('update:modelValue', false)"
        >
          {{ $t("acceptance.weighings.dialog.cancel") }}
        </v-btn>
        <v-btn
          variant="flat"
          color="primary"
          :loading="saving"
          :disabled="!valid || saving"
          @click="submit"
        >
          {{ $t("acceptance.weighings.dialog.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  MAX_KG,
  buildWeighingBody,
  emptyWeighingForm,
  localDateTime,
  netPreview,
  switchMethod,
  weighingErrors,
} from "@/components/Acceptance/weighingForm.mjs";

export default {
  name: "AcceptanceAddWeighingDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    // Save failure from the page, shown inside the dialog; the input is kept.
    error: { type: String, default: "" },
    // "Name (Role)" of the logged-in user; the API takes the verifier from the token.
    verifier: { type: String, default: "" },
  },
  emits: ["update:modelValue", "submit"],
  data: () => ({
    form: emptyWeighingForm(),
    now: new Date(),
  }),
  computed: {
    errors() {
      return weighingErrors(this.form, this.now);
    },
    valid() {
      return Object.keys(this.errors).length === 0;
    },
    netLabel() {
      const net = netPreview(this.form);
      if (net === null) return "—";
      return `${this.formatNumber(net)} kg`;
    },
    maxWeighedAt() {
      return localDateTime(this.now);
    },
  },
  watch: {
    modelValue(open) {
      this.stopClock();
      if (!open) return;
      this.now = new Date();
      this.form = emptyWeighingForm(this.now);
      // Keeps the "not in the future" check current while the dialog stays open.
      this.clock = setInterval(() => {
        this.now = new Date();
      }, 30000);
    },
  },
  created() {
    this.clock = null;
  },
  beforeUnmount() {
    this.stopClock();
  },
  methods: {
    stopClock() {
      if (this.clock) clearInterval(this.clock);
      this.clock = null;
    },
    setMethod(method) {
      // Clicking the selected method again deselects it in v-btn-toggle; keep the choice.
      if (!method || method === this.form.method) return;
      this.form = switchMethod(this.form, method);
    },
    formatNumber(value) {
      return new Intl.NumberFormat(this.$i18n.locale, {
        maximumFractionDigits: 1,
      }).format(value);
    },
    // An empty required field only disables Save; other problems show under the field.
    message(field) {
      const reason = this.errors[field];
      if (!reason || reason === "required") return [];
      return [
        this.$t(`acceptance.weighings.errors.${reason}`, {
          max: this.formatNumber(MAX_KG),
        }),
      ];
    },
    submit() {
      this.now = new Date();
      if (!this.valid || this.saving) return;
      this.$emit("submit", buildWeighingBody(this.form));
    },
  },
};
</script>

<style scoped>
.acceptance-wrap {
  white-space: normal;
  flex-wrap: wrap;
  height: auto;
}
</style>
